import type { ReactNode } from "react";
import { isApiError } from "@clubcore/api";
import { useLocale, useT } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import { hasMessage } from "../i18n/runtime";
import { Button } from "./Button";
import { Icon, type IconName } from "./Icon";

export function Spinner({ size = 20 }: { size?: number }) {
  return <span className="cc-spinner" style={{ width: size, height: size }} aria-hidden="true" />;
}

/** Announces itself to screen readers; shown in place of content that is still loading. */
export function Loading({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="cc-loading" role="status">
      <Spinner />
      <span>{label ?? t("common.loading")}</span>
    </div>
  );
}

export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div className="cc-skeleton-list" aria-hidden="true">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="cc-skeleton" />
      ))}
    </div>
  );
}

export function EmptyState({ icon = "search", title, children, action }: { icon?: IconName; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="cc-empty">
      <span className="cc-empty__icon">
        <Icon name={icon} size={28} />
      </span>
      <h2 className="cc-empty__title">{title}</h2>
      {children ? <p className="cc-empty__body">{children}</p> : null}
      {action}
    </div>
  );
}

const ERROR_KEYS: Record<string, MessageKey> = {
  network: "error.network",
  forbidden: "error.forbidden",
  not_found: "error.notFound",
  server: "error.server",
  rate_limited: "error.rateLimited",
};

/** Facts attached to an error that a message may mention (`{allowed}`, `{limit}`...). */
function paramsOf(details: Record<string, unknown>): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(details)) {
    if (typeof value === "string" || typeof value === "number") params[key] = value;
  }
  return params;
}

/**
 * Human message for any thrown value. Known error codes have translated text in the catalog
 * (`apiError.<code>`); an unknown code falls back to the server's own message where that is meant for
 * people, and to a generic line for network and server failures.
 */
export function useErrorMessage() {
  const t = useT();
  const { locale } = useLocale();
  return (error: unknown): string => {
    if (isApiError(error)) {
      if (error.kind === "rate_limited") {
        return error.retryAfterSeconds ? t("error.rateLimitedMinutes", { minutes: Math.ceil(error.retryAfterSeconds / 60) }) : t("error.rateLimited");
      }
      const key = `apiError.${error.code}`;
      if (hasMessage(locale, key)) return t(key as MessageKey, paramsOf(error.details));
      if (error.kind === "validation" || error.kind === "conflict" || error.kind === "http" || error.kind === "unauthorized") {
        return error.message;
      }
      const generic = ERROR_KEYS[error.kind];
      return generic ? t(generic) : error.message;
    }
    return t("error.unknown");
  };
}

export function ErrorState({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const t = useT();
  const message = useErrorMessage()(error);
  return (
    <div className="cc-empty cc-empty--error" role="alert">
      <span className="cc-empty__icon cc-empty__icon--error">
        <Icon name="alert" size={28} />
      </span>
      <h2 className="cc-empty__title">{title ?? t("error.title")}</h2>
      <p className="cc-empty__body">{message}</p>
      {onRetry ? (
        <Button onClick={onRetry} variant="secondary">
          {t("common.retry")}
        </Button>
      ) : null}
    </div>
  );
}

export function Badge({ tone = "neutral", children }: { tone?: "neutral" | "success" | "warning" | "danger" | "info" | "accent"; children: ReactNode }) {
  return <span className={`cc-badge cc-badge--${tone}`}>{children}</span>;
}
